import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  VStack,
  Text,
  Pressable,
  Image,
  HStack,
  Button,
  Tooltip,
} from "native-base";
import dayjs, { Dayjs } from "dayjs";
import { CalendarIcon } from "@mui/x-date-pickers";
import { Dimensions } from "react-native";
import Moment from "moment";
import { Loc } from "../../App/Loc";
import * as AV from "../../AVCore/AVCore";
import { useNavigate } from "react-router-dom";
import { useSelector, RootStateOrAny } from "react-redux";
import AVDescription from "./AVDescription";
import EventTitle from "../../components/Events/EventTitle";

const AVHeaderTitle = ({
  Entity,
  EntityType,
  homeTeam,
  guestTeam,
  showGameInfo,
  leagueGameEntity,
  googlemap,
  weatherURL,
  weather,
  setIsImagePopup,
  seriesEvent,
  isAdminView,
  setClickedUrl,
  setShowConfirmLinkPopup,
}) => {
  const navigate = useNavigate();
  const dimensions = Dimensions.get("window");
  const isMobile = dimensions.width <= 600 || false;
  const [fieldname, setFieldName] = useState("");
  const [mapUrl, setMapUrl] = useState("");
  const currentUser = useSelector(
    (state: RootStateOrAny) => state?.user?.user[0],
  );
  const entitydatalist = useSelector(
    (state: RootStateOrAny) => state.user.entitydatalist,
  );

  useEffect(() => {
    let isMounted = true;
    if (isMounted) {
      setFieldName(Entity?.location?.field?.name);
    }
    return () => {
      isMounted = false; // Set the flag to false when the component is unmounted
      // Cancel subscriptions or perform any cleanup tasks here
    };
  }, [Entity]);

  const getDaysLeft = (dateStr: string): string => {
    const targetDate = new Date(dateStr);
    const today = new Date();

    // Reset time for accurate day comparison
    today.setHours(0, 0, 0, 0);
    targetDate.setHours(0, 0, 0, 0);

    const diffTime = targetDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays < 0) return Loc.currentLang.feeCollect.expired;
    if (diffDays === 0) return Loc.currentLang.feeCollect.today;

    return `${diffDays} ${diffDays > 1 ? Loc.currentLang.feeCollect.daysLeft : Loc.currentLang.feeCollect.dayLeft}`;
  };

  const TeamTitle = ({ Entity }) => {
    const daysLeft = getDaysLeft(Entity?.end);
    // Find current user's participant entry
    const userParticipant = Entity?.participants?.find(
      (x) => x?.id === currentUser?.id,
    );

    // Check if fully paid
    const isPaid =
      Entity?.type === "Collect" && userParticipant?.amount === Entity?.goal;

    return (
      <>
        <VStack height={"auto"} width={"90%"} left={isMobile ? "24px" : "48px"}>
          <VStack>
            <Tooltip label={Entity?.name} openDelay={500}>
              <Text
                fontSize={"36px"}
                numberOfLines={1}
                width={Entity?.type === "Collect" ? "95%" : "65%"}
              >
                {Entity?.name}
              </Text>
            </Tooltip>

            {Entity?.type !== "Collect" && Entity?.profile?.text && (
              <Tooltip label={Entity?.profile?.text} openDelay={500}>
                <Text
                  fontSize={"18px"}
                  fontWeight={500}
                  numberOfLines={1}
                  width={"65%"}
                >
                  <AVDescription text={Entity?.profile?.text} />
                </Text>
              </Tooltip>
            )}

            <HStack space={1}>
              {Entity?.links &&
                Entity?.links?.map((item, index) => (
                  <Pressable
                    key={index}
                    //onPress={() => window.open(item?.url)}
                    onPress={() => {
                      if (item?.url) {
                        setClickedUrl?.(item.url);
                        setShowConfirmLinkPopup?.(true);
                      }
                    }}
                    marginTop={"12px"}
                  >
                    <img
                      src={
                        item?.icon?.trim()?.length > 0
                          ? item?.icon
                          : "https://cdn-icons-png.flaticon.com/512/2985/2985013.png"
                      }
                      alt={`Icon ${index}`}
                      height={"24px"}
                      width={"24px"}
                    />
                  </Pressable>
                ))}
            </HStack>

            {Entity?.parent && (
              <Box alignSelf="flex-start">
                <Pressable
                  onPress={() => {
                    if (Entity?.parent?.type === "PlayerOrg") {
                      navigate(
                        `/season/${Entity?.parent?.id}/${Entity?.parent?.region}`,
                        {
                          state: { isCreate: false, team: Entity?.parent },
                        },
                      );
                    } else if (Entity?.parent?.type === "Team") {
                      navigate(
                        `/teams/${Entity?.parent?.id}/${Entity?.parent?.region}`,
                        {
                          state: { isCreate: false, team: Entity?.parent },
                        },
                      );
                    } else if (Entity?.parent?.type === "Event" || Entity?.parent?.type === "Game") {
                      AV.AVEntity.get(Entity.parent).then(() => {
                        let newSeriesId = undefined;

                        const selectedEvent = currentUser?.events?.find(
                          (x) => x.id === Entity.parent.id,
                        );

                        if (
                          selectedEvent?.linkType === "Meeting" &&
                          selectedEvent?.events?.length > 0
                        ) {
                          newSeriesId = selectedEvent.events[0].id;
                        }

                        navigate(
                          `/Events/${Entity.parent.id}/${Entity?.parent?.region}/${newSeriesId}`,
                          {
                            state: { isCreate: false, team: Entity?.parent },
                          },
                        );
                      });
                    } else {
                      navigate(
                        `/season/${Entity?.parent?.id}/${Entity?.parent?.region}`,
                        {
                          state: { isCreate: false, team: Entity?.parent },
                        },
                      );
                    }
                  }}
                  marginTop={"12px"}
                >
                  <Text
                    fontSize={"16px"}
                    fontWeight={400}
                    color="#00A7BC"
                    underline
                  >
                    {Entity?.parent?.title}
                  </Text>
                </Pressable>
              </Box>
            )}

            {Entity?.type === "Collect" && (
              <HStack justifyContent="space-between" alignItems="center" mt={3}>
                <HStack space={1} alignItems="baseline">
                  <Text fontSize="lg" fontWeight="700">
                    ${Entity?.goal}
                  </Text>
                  {isAdminView && (
                    <Text fontSize="xs" color="gray.600">
                      {Entity.options.collectType === "Members"
                        ? Loc.currentLang.feeCollect.perMember
                        : Entity.options.collectType === "Players"
                          ? Loc.currentLang.feeCollect.perPlayer
                          : Entity.options.collectType === "Participants"
                            ? Loc.currentLang.feeCollect.perParticipant
                            : Entity.options.collectType === "Both"
                              ? Loc.currentLang.feeCollect.both
                              : ""}
                    </Text>
                  )}
                </HStack>

                <HStack space={1} alignItems="center">
                  <CalendarIcon style={{ color: "#00A7BC" }} />
                  <Text fontSize="lg" fontWeight="700">
                    {daysLeft}
                  </Text>
                </HStack>
              </HStack>
            )}
          </VStack>

          {/* ===== Paid Status (Centered) ===== */}
          {Entity?.type === "Collect" && !isAdminView && (
            <HStack
              width="100%"
              justifyContent="center"
              alignItems="center"
              mt={4}
            >
              <Text
                fontSize="lg"
                fontWeight="700"
                color={!userParticipant ? "#ff0000" : "#32AE78"}
              >
                {!userParticipant
                  ? Loc.currentLang.feeCollect.memberRemoved
                  : isPaid
                    ? Loc.currentLang.feeCollect.thankyouPayment
                    : ""}
              </Text>
            </HStack>
          )}
        </VStack>
      </>
    );
  };

  const SeasonTitle = ({ Entity }) => {
    return (
      <>
        <VStack
          height={"auto"}
          width={"100%"}
          left={isMobile ? "24px" : "48px"}
        >
          <VStack>
            <Text fontSize={"24px"} numberOfLines={1} width={"65%"}>
              {Entity?.name}
            </Text>

            <Text fontSize={"36px"} numberOfLines={1} width={"65%"}>
              {Entity?.season}
            </Text>

            {Entity?.profile?.text && (
              <Text
                fontSize={"18px"}
                fontWeight={500}
                numberOfLines={1}
                width={"65%"}
              >
                <AVDescription text={Entity?.profile?.text} />
              </Text>
            )}

            <HStack space={1}>
              {Entity?.links &&
                Entity?.links?.map((item, index) => (
                  <Pressable
                    key={index}
                    onPress={() => {
                      if (item?.url) {
                        setClickedUrl?.(item.url);
                        setShowConfirmLinkPopup?.(true);
                      }
                    }}
                    marginTop={"12px"}
                  >
                    <img
                      src={
                        item?.icon?.trim()?.length > 0
                          ? item?.icon
                          : "https://cdn-icons-png.flaticon.com/512/2985/2985013.png"
                      }
                      alt={`Icon ${index}`}
                      height={"24px"}
                      width={"24px"}
                    />
                  </Pressable>
                ))}
            </HStack>

            {Entity?.parent && (
              <Box alignSelf="flex-start">
                <Pressable
                  onPress={() => {
                    Entity?.parent?.type == "PlayerOrg"
                      ? navigate(
                          `/season/${Entity?.parent?.id}/${Entity?.parent?.region}`,
                          {
                            state: { isCreate: false, team: Entity?.parent },
                          },
                        )
                      : Entity?.parent?.type == "Team"
                        ? navigate(
                            `/teams/${Entity?.parent?.id}/${Entity?.parent?.region}`,
                            {
                              state: { isCreate: false, team: Entity?.parent },
                            },
                          )
                        : navigate(
                            `/season/${Entity?.parent?.id}/${Entity?.parent?.region}`,
                            {
                              state: { isCreate: false, team: Entity?.parent },
                            },
                          );
                  }}
                  marginTop={"12px"}
                >
                  <Text
                    fontSize={"16px"}
                    fontWeight={400}
                    color="#00A7BC"
                    underline
                  >
                    {Entity?.parent?.data
                      ? Entity?.parent?.data
                      : Entity?.parent?.title}
                  </Text>
                </Pressable>
              </Box>
            )}
          </VStack>
        </VStack>
      </>
    );
  };

  const ClubSchoolTitle = ({ Entity }) => {
    return (
      <>
        <VStack
          height={"auto"}
          width={"90%"}
          left={isMobile ? "24px" : "48px"}
          zIndex={-3}
        >
          <VStack>
            <Text fontSize={"36px"} numberOfLines={1} width={"65%"}>
              {Entity?.id === "b495d4e0-bf52-4690-8dc0-677db954aa7f"
                ? Loc.currentLang.menu.feedback
                : Entity?.id === "b495d4e0-bf52-4690-8dc0-677db954aa71"
                  ? Loc.currentLang.menu.bugReports
                  : Entity?.id === "b495d4e0-bf52-4690-8dc0-677db954aa72"
                    ? Loc.currentLang.menu.featureRequests
                    : Entity?.name}
            </Text>

            <Text
              fontSize={"18px"}
              fontWeight={500}
              numberOfLines={
                Entity?.id === "b495d4e0-bf52-4690-8dc0-677db954aa7f" ||
                "b495d4e0-bf52-4690-8dc0-677db954aa71" ||
                "b495d4e0-bf52-4690-8dc0-677db954aa72"
                  ? 3
                  : 1
              }
              width={
                Entity?.id === "b495d4e0-bf52-4690-8dc0-677db954aa7f" ||
                "b495d4e0-bf52-4690-8dc0-677db954aa71" ||
                "b495d4e0-bf52-4690-8dc0-677db954aa72"
                  ? "100%"
                  : "65%"
              }
            >
              <AVDescription
                text={
                  Entity?.id === "78b5b063-fd3e-4925-92bd-2a1888c6a46a"
                    ? Loc.currentLang.feedBack.actavivoTitle
                    : Entity?.id === "b495d4e0-bf52-4690-8dc0-677db954aa7f"
                      ? Loc.currentLang.feedBack.feedbackTitle
                      : Entity?.id === "b495d4e0-bf52-4690-8dc0-677db954aa71"
                        ? Loc.currentLang.feedBack.bugReportTitle
                        : Entity?.id === "b495d4e0-bf52-4690-8dc0-677db954aa72"
                          ? Loc.currentLang.feedBack.featureRequestTitle
                          : Entity?.profile?.text
                }
              />
            </Text>

            <Text
              fontSize={"16px"}
              numberOfLines={1}
              color={"rgba(0,0,0,0.5)"}
              width={"65%"}
            >
              {Entity?.location?.normalized}
            </Text>

            <HStack justifyContent={"space-between"}>
              <VStack>
                <HStack space={1}>
                  {Entity?.links &&
                    Entity?.links?.map((item, index) => (
                      <Pressable
                        key={index}
                        onPress={() => {
                          if (item?.url) {
                            setClickedUrl?.(item.url);
                            setShowConfirmLinkPopup?.(true);
                          }
                        }}
                        marginTop={"12px"}
                      >
                        <img
                          src={
                            item?.icon?.trim()?.length > 0
                              ? item?.icon
                              : "https://cdn-icons-png.flaticon.com/512/2985/2985013.png"
                          }
                          alt={`Icon ${index}`}
                          height={"24px"}
                          width={"24px"}
                        />
                      </Pressable>
                    ))}
                </HStack>

                <HStack space={3} marginTop={"24px"} marginBottom={"10px"}>
                  {fieldname && (
                    <HStack space={3}>
                      <Text
                        fontSize={"16px"}
                        color={"rgba(0, 0, 0, 0.50)"}
                        numberOfLines={1}
                      >
                        {Loc.currentLang.menu.fieldOrRoomText}
                      </Text>
                      <Tooltip label={fieldname} openDelay={500}>
                        <Text
                          fontSize={16}
                          fontWeight={500}
                          color={"black"}
                          numberOfLines={1}
                          textTransform={"capitalize"}
                        >
                          {fieldname}
                        </Text>
                      </Tooltip>
                    </HStack>
                  )}

                  {Entity?.location?.siteMap && (
                    <HStack>
                      <Pressable onPress={() => setIsImagePopup(true)}>
                        <Text
                          fontSize={"15px"}
                          color={"#00A7BC"}
                          fontWeight={"500"}
                        >
                          {fieldname
                            ? Loc.currentLang.menu.viewFieldOrRoom
                            : ""}
                        </Text>
                      </Pressable>
                    </HStack>
                  )}
                </HStack>

                {Entity?.parent?.id !==
                  "78b5b063-fd3e-4925-92bd-2a1888c6a46a" && (
                  <Box alignSelf="flex-start">
                    <Pressable
                      onPress={() => {
                        Entity?.parent?.type == "PlayerOrg"
                          ? navigate(
                              `/season/${Entity?.parent?.id}/${Entity?.parent?.region}`,
                              {
                                state: {
                                  isCreate: false,
                                  team: Entity?.parent,
                                },
                              },
                            )
                          : Entity?.parent?.type == "Team"
                            ? navigate(
                                `/teams/${Entity?.parent?.id}/${Entity?.parent?.region}`,
                                {
                                  state: {
                                    isCreate: false,
                                    team: Entity?.parent,
                                  },
                                },
                              )
                            : navigate(
                                `/season/${Entity?.parent?.id}/${Entity?.parent?.region}`,
                                {
                                  state: {
                                    isCreate: false,
                                    team: Entity?.parent,
                                  },
                                },
                              );
                      }}
                      marginTop={"12px"}
                    >
                      <Text
                        fontSize={"16px"}
                        fontWeight={400}
                        color="#00A7BC"
                        underline
                      >
                        {Entity?.parent?.title}
                      </Text>
                    </Pressable>
                  </Box>
                )}

                {Entity?.parent?.id ==
                  "78b5b063-fd3e-4925-92bd-2a1888c6a46a" && (
                  <Box alignSelf="flex-start">
                    <Pressable
                      onPress={() => {
                        navigate(-1);
                      }}
                      marginTop={"12px"}
                    >
                      <Text
                        fontSize={"16px"}
                        fontWeight={400}
                        color="#00A7BC"
                        underline
                      >
                        {Entity?.parent?.title}
                      </Text>
                    </Pressable>
                  </Box>
                )}
              </VStack>

              {Entity?.location?.normalized?.trim()?.length > 0 &&
                Entity?.location?.normalized?.trim()?.length !== undefined && (
                  <Pressable
                    height={"95px"}
                    width={"150px"}
                    borderColor={"gray.400"}
                    borderWidth={"1px"}
                    borderRadius={"10px"}
                    onPress={() =>
                      window.open(
                        "https://www.google.com/maps/search/?api=1&query=" +
                          Entity?.location?.normalized +
                          "&query_place_id=" +
                          Entity?.location?.placeId,
                        "_blank",
                        "noreferrer",
                      )
                    }
                  >
                    <Image
                      source={{ uri: googlemap }}
                      style={{
                        height: "100%",
                        width: "100%",
                        borderRadius: 10,
                        margin: "auto",
                      }}
                      alt="googlemap"
                    />
                  </Pressable>
                )}
            </HStack>
          </VStack>
        </VStack>
      </>
    );
  };

  const Profile = ({ Entity }) => {
    return (
      <>
        <VStack
          height={"auto"}
          width={"90%"}
          left={isMobile ? "24px" : "48px"}
          zIndex={-3}
        >
          <VStack>
            <Text
              fontSize={"36px"}
              fontWeight="medium"
              fontFamily="Roboto"
              numberOfLines={1}
              width={"65%"}
            >
              {Entity?.name}
            </Text>

            {Entity?.profile?.text && (
              <Text
                fontSize={"18px"}
                fontWeight={500}
                numberOfLines={1}
                width={"65%"}
              >
                <AVDescription text={Entity?.profile?.text} />
              </Text>
            )}

            <Text fontSize={"16px"} color={"rgba(0,0,0,0.5)"} width={"65%"}>
              {Entity?.address?.normalized}
            </Text>

            <HStack justifyContent={"space-between"}>
              <HStack space={1}>
                {Entity?.links &&
                  Entity?.links?.map((item, index) => (
                    <Pressable
                      key={index}
                      onPress={() => {
                        if (item?.url) {
                          setClickedUrl?.(item.url);
                          setShowConfirmLinkPopup?.(true);
                        }
                      }}
                      marginTop={"12px"}
                    >
                      <img
                        src={
                          item?.icon?.trim()?.length > 0
                            ? item?.icon
                            : "https://cdn-icons-png.flaticon.com/512/2985/2985013.png"
                        }
                        alt={`Icon ${index}`}
                        height={"24px"}
                        width={"24px"}
                      />
                    </Pressable>
                  ))}
              </HStack>
            </HStack>
          </VStack>
        </VStack>
      </>
    );
  };

  return (
    <>
      {(EntityType?.type === "Team" ||
        EntityType?.type === "Collect" ||
        EntityType?.type === "LeagueOwner" ||
        EntityType?.type === "LeagueSeason") &&
        Entity?.subType !== "Club" &&
        Entity?.type !== "Event" &&
        Entity?.type !== "Game" &&
        Entity?.type !== "LeagueGameBase" && <TeamTitle Entity={Entity} />}
      {EntityType?.type === "PlayerOrg" &&
        Entity?.type !== "Event" &&
        Entity?.type !== "Game" &&
        Entity?.type !== "LeagueGameBase" && <SeasonTitle Entity={Entity} />}
      {(EntityType?.type === "Club" ||
        Entity?.subType === "Club" ||
        EntityType?.type === "School" ||
        EntityType?.type === "SchoolYear" ||
        EntityType?.type === "Community" ||
        EntityType?.type === "Neighborhood" ||
        EntityType?.type === "Organization" ||
        EntityType?.type === "Group") &&
        Entity?.type !== "Event" &&
        Entity?.type !== "Game" &&
        Entity?.type !== "LeagueGameBase" && (
          <ClubSchoolTitle Entity={Entity} />
        )}
      {(EntityType?.type === "Person" || EntityType?.type === "Pet") &&
        Entity?.type !== "Event" &&
        Entity?.type !== "Game" &&
        Entity?.type !== "LeagueGameBase" && <Profile Entity={Entity} />}
      {(Entity?.type === "Event" ||
        Entity?.type === "Game" ||
        Entity?.type === "LeagueGameBase") && (
        <EventTitle
          entity={Entity}
          homeTeam={homeTeam}
          guestTeam={guestTeam}
          showGameInfo={showGameInfo}
          leagueGameEntity={leagueGameEntity}
          setIsImagePopup={setIsImagePopup}
          setClickedUrl={setClickedUrl}
          setShowConfirmLinkPopup={setShowConfirmLinkPopup}
        />
      )}
    </>
  );
};

export default AVHeaderTitle;
